/**
 * FeaturedCompletionsHero — owner-curated 8-card grid showcased on the
 * NoJo Enterprise homepage. Surfaces shipped highlights from BOTH
 * sister services merged together (per owner directive: "stuff we do
 * as a company. It doesn't have to be like separated").
 *
 * Each card → "See it live" link to the corresponding sister-site page.
 * Five-Model AI Team is internal — it links to /our-team.
 */
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  Cpu,
  FileText,
  Layers,
  Map,
  Network,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import Reveal from "@/components/motion/Reveal";
import BrandGraphic from "@/components/motion/BrandGraphic";
import { FEATURED_COMPLETIONS } from "@/data/featuredCompletions";
import { withLang } from "@/lib/externalLink";

const ICONS = { Cpu, FileText, Layers, Map, Network, ShieldCheck, Sparkles };

const SERVICE_LABELS = {
  japanetccard: "JapanETCcard.com",
  japantollreceipts: "JapanTollReceipts.jp",
  japanchangemoney: "JapanChangeMoney.com",
  shared: "NoJo Enterprise",
};

export default function FeaturedCompletionsHero({ lang }) {
  const isJa = lang === "ja";
  return (
    <section
      className="bg-[#F8F9FA] border-y border-[#E2E8F0]"
      data-testid="home-featured-completions"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] font-mono-display text-[#0E5FB0] uppercase font-bold mb-3">
              <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />
              {isJa ? "完了済みの主な成果" : "Featured Completions"}
            </div>
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tighter leading-[1.1]",
              ].join(" ")}
            >
              {isJa
                ? "私たちがこれまでに届けてきたもの。"
                : "What we've already shipped."}
            </h2>
            <p className="mt-3 text-[14.5px] leading-[1.8] text-[#475569] max-w-2xl">
              {isJa
                ? "JapanETCcard と JapanTollReceipts の両サービスで、お客様に最も大きなインパクトを与えてきた取り組みを集めました。"
                : "The highest-impact items we've shipped across JapanETCcard and JapanTollReceipts — pulled together so you can see what NoJo actually delivers."}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold px-2.5 py-1 rounded-full text-[#2F8F3C] bg-[#ECF6EC] shrink-0">
            <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} />
            {isJa ? "出荷済み" : "Shipped"}
          </span>
        </div>

        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {FEATURED_COMPLETIONS.map((item, i) => {
            const Icon = ICONS[item.iconKey] || Sparkles;
            const serviceLabel = SERVICE_LABELS[item.service] || "";
            const isInternal = !!item.internalHref;
            const cardBody = (
              <>
                {/* Visual panel */}
                <div
                  className="relative aspect-[5/3] w-full overflow-hidden flex items-center justify-center px-5"
                  style={{ backgroundColor: item.tone }}
                >
                  <BrandGraphic
                    variant="wave"
                    color={item.accentText}
                    className="absolute inset-0 w-full h-full opacity-[0.18] pointer-events-none"
                  />
                  <Icon
                    className="relative h-14 w-14 shrink-0 opacity-90"
                    style={{ color: item.accentText }}
                    strokeWidth={1.5}
                  />
                  <div className="relative ml-4 flex-1 min-w-0">
                    <div
                      className="text-[9.5px] tracking-[0.26em] font-mono-display uppercase font-bold"
                      style={{ color: item.accentText, opacity: 0.75 }}
                    >
                      {isJa ? item.categoryJa : item.categoryEn}
                    </div>
                    <div
                      className="mt-1 text-[15px] md:text-[16px] font-bold tracking-tight leading-tight line-clamp-2"
                      style={{ color: item.accentText }}
                    >
                      {isJa ? item.titleJa : item.titleEn}
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="flex-1 flex flex-col p-5">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span
                      className="text-[10px] tracking-[0.18em] font-mono-display uppercase font-bold px-2 py-0.5 rounded"
                      style={{
                        color: item.tone,
                        backgroundColor: `${item.tone}15`,
                      }}
                    >
                      {isJa ? item.categoryJa : item.categoryEn}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] font-mono-display uppercase font-bold text-[#2F8F3C]">
                      <CheckCircle2 className="h-2.5 w-2.5" strokeWidth={2.5} />
                      {isJa ? "完了" : "Completed"}
                    </span>
                  </div>
                  <h3 className="text-[15px] md:text-[16px] font-bold text-[#0F1D2D] tracking-tight leading-snug">
                    {isJa ? item.titleJa : item.titleEn}
                  </h3>
                  <p className="mt-2 text-[12.5px] leading-[1.7] text-[#475569] flex-1">
                    {isJa ? item.summaryJa : item.summaryEn}
                  </p>
                  <div className="mt-4 pt-3 border-t border-[#E2E8F0] flex items-center justify-between gap-2">
                    <span
                      className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold tracking-tight"
                      style={{ color: item.tone }}
                    >
                      {isInternal
                        ? isJa ? item.ctaJa : item.ctaEn
                        : isJa ? "ライブで見る" : "See it live"}
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.25} />
                    </span>
                    <span className="text-[9.5px] tracking-[0.2em] font-mono-display uppercase text-[#94A3B8] truncate">
                      {serviceLabel}
                    </span>
                  </div>
                </div>
              </>
            );
            const sharedClass =
              "group h-full flex flex-col rounded-2xl bg-white ring-1 ring-[#E2E8F0] overflow-hidden transition-all hover:-translate-y-1 hover:ring-transparent hover:shadow-[0_30px_65px_-30px_rgba(15,29,45,0.4)]";

            return (
              <Reveal as="li" variant="up" delay={i * 60} key={item.id}>
                {isInternal ? (
                  <Link
                    to={`/${lang}${item.internalHref}`}
                    data-testid={`home-featured-card-${item.id}`}
                    className={sharedClass}
                  >
                    {cardBody}
                  </Link>
                ) : (
                  <a
                    href={item.noLangMirror ? item.href : withLang(item.href, lang)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`home-featured-card-${item.id}`}
                    className={sharedClass}
                  >
                    {cardBody}
                  </a>
                )}
              </Reveal>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
